import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  Ban,
  Check,
  Circle,
  CircleCheck,
  CreditCard,
  GitBranch,
  Headset,
  KeyRound,
  Lock,
  LogOut,
  Mail,
  Minus,
  Pencil,
  Play,
  Plus,
  Settings2,
  ShieldCheck,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  ASSIGNABLE_OPS_ROLES,
  BRAND_IDS,
  OPS_PASSWORD_MAX_LENGTH,
  OPS_PASSWORD_MIN_LENGTH,
  type OpsUserRecord,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { expandOpsRoles } from '../auth/opsRoles.ts';
import {
  useCreateOpsUser,
  useDeleteSalesProfile,
  useDisableOpsUser,
  useEnableOpsUser,
  useInviteOpsUserPassword,
  useOpsUsers,
  useRevokeOpsUserSessions,
  useResetOpsUserPassword,
  useSetOpsUserPassword,
  useSalesProfile,
  useSetSalesProfile,
  useUpdateOpsUser,
} from '../api/opsUsers.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import { OpsUserSheet } from '../components/OpsUserSheet.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT, type I18nKey } from '../i18n/index.ts';
import {
  Button,
  buttonClass,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/ops-users` — admin page for the ops user directory. Epic #109 P3.18.
 *
 * The surface is modelled as an access-control matrix with depth: the base
 * layer stays a dense permission grid, the selected-user inspector creates a
 * second layer, and create/edit/reset flows open as sheets or modals.
 *
 * One-click salesperson provisioning is deliberately *not* here. It creates an
 * ops user, but what an operator wants out of it is an invite code with a
 * salesperson attached, so its entry point lives on the invite-codes page
 * (#2022) and the drawer itself in `components/ProvisionSalespersonSheet.tsx`.
 */

const ROLE_OPTIONS: string[] = [...ASSIGNABLE_OPS_ROLES];

interface RoleMeta {
  readonly shortLabel: string;
  readonly matrixLabel: string;
  readonly icon: LucideIcon;
}

const ROLE_META: Record<string, RoleMeta> = {
  'ops-admin': { shortLabel: 'ADMIN', matrixLabel: 'ADMIN', icon: ShieldCheck },
  'ops-support': { shortLabel: 'SUPPORT', matrixLabel: 'SUP', icon: Headset },
  'ops-sales-lead': { shortLabel: 'SALES LEAD', matrixLabel: 'LEAD', icon: UserCog },
  'ops-customer-manager': { shortLabel: 'CUSTOMER', matrixLabel: 'CUST', icon: Users },
  'ops-finance-manager': { shortLabel: 'FINANCE', matrixLabel: 'FIN', icon: CreditCard },
  'ops-supply-demand-manager': { shortLabel: 'SUPPLY', matrixLabel: 'SPLY', icon: GitBranch },
  'ops-runtime-operator': { shortLabel: 'RUNTIME', matrixLabel: 'RUN', icon: Settings2 },
};

function roleMeta(role: string): RoleMeta {
  return ROLE_META[role] ?? { shortLabel: role, matrixLabel: role, icon: BadgeCheck };
}

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeAssignableRoles(roles: readonly string[]): string[] {
  const selected = new Set(roles);
  return ROLE_OPTIONS.filter((role) => selected.has(role));
}

function sameStringList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function feishuDisplayName(user: OpsUserRecord): string {
  return user.feishu_name ?? user.feishu_enterprise_email ?? user.feishu_open_id ?? '';
}

function hasFeishuBinding(user: OpsUserRecord): boolean {
  return user.feishu_open_id !== null;
}

/**
 * An ops account can authenticate when it has either a seeded password
 * credential or a Feishu SSO binding. Feishu SSO is a complete, independent
 * login path (platform-api mints the ops_users row on first Feishu login and
 * never touches ops_password_credentials), so a Feishu-bound account without a
 * password is fully functional — the account-health status must not flag it.
 */
function canAuthenticate(user: OpsUserRecord): boolean {
  return user.has_password || hasFeishuBinding(user);
}

function statusVariant(user: OpsUserRecord): 'ok' | 'warning' | 'error' | 'neutral' {
  if (user.disabled_at !== null) return 'error';
  if (!canAuthenticate(user)) return 'error';
  return 'ok';
}

/**
 * Single source of truth for the account-health chip label, shared by the
 * table row and the detail-sheet header so the two never disagree (a red
 * "locked out" row must not read "Active" in the inspector).
 */
function statusLabelKey(user: OpsUserRecord): I18nKey {
  if (user.disabled_at !== null) return 'ops_users.status.disabled';
  if (!canAuthenticate(user)) return 'ops_users.status.locked_out';
  return 'ops_users.status.active';
}

export function OpsUsersPage(): JSX.Element {
  const t = useT();
  const query = useOpsUsers();
  const users = query.data?.users ?? [];
  const directPasswordSetAllowed = query.data?.direct_password_set_allowed ?? false;
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OpsUserRecord | null>(null);
  const [resetTarget, setResetTarget] = useState<OpsUserRecord | null>(null);
  const [setPasswordTarget, setSetPasswordTarget] = useState<OpsUserRecord | null>(null);
  const [inviteTarget, setInviteTarget] = useState<OpsUserRecord | null>(null);

  const selectedUser =
    selectedUserId === null ? null : users.find((user) => user.id === selectedUserId) ?? null;
  const activeCount = users.filter((user) => user.disabled_at === null).length;
  const disabledCount = users.filter((user) => user.disabled_at !== null).length;
  const lockedOutCount = users.filter(
    (user) => user.disabled_at === null && !canAuthenticate(user),
  ).length;
  const unboundCount = users.filter((user) => !hasFeishuBinding(user)).length;

  return (
    <section className="flex flex-col gap-6" data-testid="ops-users-page">
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip variant="neutral" label={`${users.length} ${t('ops_users.metric.total')}`} />
        <StatusChip variant="ok" label={`${activeCount} ${t('ops_users.metric.active')}`} />
        <StatusChip
          variant={disabledCount > 0 ? 'error' : 'neutral'}
          label={`${disabledCount} ${t('ops_users.metric.disabled')}`}
        />
        <StatusChip
          variant={lockedOutCount > 0 ? 'error' : 'neutral'}
          label={`${lockedOutCount} ${t('ops_users.metric.locked_out')}`}
        />
        <StatusChip
          variant={unboundCount > 0 ? 'warning' : 'neutral'}
          label={`${unboundCount} ${t('ops_users.metric.unbound')}`}
        />
      </div>
      <PageHeader
        title={t('ops_users.title')}
        sub={t('ops_users.subtitle')}
        actions={
          <>
            <Link
              to="/governance/permissions"
              className={buttonClass('secondary')}
              data-testid="ops-users-permissions-link"
            >
              <Lock aria-hidden="true" className="h-4 w-4" />
              {t('ops_users.action.permissions')}
            </Link>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreateOpen(true)}
              data-testid="ops-users-open-create"
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('ops_users.action.create')}
            </Button>
          </>
        }
      />

      <Panel
        caption={t('ops_users.matrix.title')}
        testId="ops-users-list-card"
        actions={
          <StatusChip variant="neutral" label={`${ROLE_OPTIONS.length} ${t('ops_users.matrix.roles')}`} />
        }
      >
        <div className="flex flex-col gap-3 p-4">
          <p className="m-0 text-dense text-paper-low">{t('ops_users.matrix.subtitle')}</p>
          {query.isLoading ? (
            <p className="m-0 text-dense text-paper-low" data-testid="ops-users-loading">
              {t('ops_users.loading')}
            </p>
          ) : query.isError ? (
            <p className="m-0 text-dense text-bad" role="alert" data-testid="ops-users-error">
              {t('ops_users.error')}
            </p>
          ) : users.length === 0 ? (
            <div className="rounded-lg bg-well">
              <EmptyState
                testId="ops-users-empty"
                title={t('ops_users.empty')}
                action={
                  <Button type="button" onClick={() => setCreateOpen(true)}>
                    <Plus aria-hidden="true" className="h-4 w-4" />
                    {t('ops_users.action.create')}
                  </Button>
                }
              />
            </div>
          ) : (
            <UsersTable
              users={users}
              selectedUserId={selectedUser?.id ?? null}
              directPasswordSetAllowed={directPasswordSetAllowed}
              onSelect={setSelectedUserId}
              onEdit={setEditTarget}
              onReset={setResetTarget}
              onSetPassword={setSetPasswordTarget}
              onInvite={setInviteTarget}
            />
          )}
        </div>
      </Panel>

      {createOpen ? <CreateUserSheet onClose={() => setCreateOpen(false)} /> : null}
      {selectedUser !== null ? (
        <SelectedUserSheet
          user={selectedUser}
          onClose={() => setSelectedUserId(null)}
          onEdit={setEditTarget}
          onReset={setResetTarget}
          onSetPassword={setSetPasswordTarget}
          onInvite={setInviteTarget}
          directPasswordSetAllowed={directPasswordSetAllowed}
        />
      ) : null}
      {editTarget !== null ? (
        <EditUserSheet user={editTarget} onClose={() => setEditTarget(null)} />
      ) : null}
      {resetTarget !== null ? (
        <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
      ) : null}
      {setPasswordTarget !== null ? (
        <SetPasswordModal user={setPasswordTarget} onClose={() => setSetPasswordTarget(null)} />
      ) : null}
      {inviteTarget !== null ? (
        <InvitePasswordModal user={inviteTarget} onClose={() => setInviteTarget(null)} />
      ) : null}
    </section>
  );
}

function RolePicker({
  selected,
  onChange,
  disabled = false,
}: {
  readonly selected: readonly string[];
  readonly onChange: (next: string[]) => void;
  readonly disabled?: boolean;
}): JSX.Element {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {ROLE_OPTIONS.map((role) => {
        const checked = selected.includes(role);
        const meta = roleMeta(role);
        return (
          <label
            key={role}
            className={
              checked
                ? 'group relative flex min-w-0 cursor-pointer items-center gap-2 rounded-lg bg-signal-dim px-3 py-2 text-paper ring-1 ring-signal/60 transition-colors duration-120'
                : 'group relative flex min-w-0 cursor-pointer items-center gap-2 rounded-lg bg-card px-3 py-2 text-paper-mid ring-1 ring-line transition-colors duration-120 hover:bg-active hover:text-paper'
            }
          >
            <input
              type="checkbox"
              className="peer sr-only"
              aria-label={role}
              checked={checked}
              disabled={disabled}
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  onChange([...selected.filter((r) => r !== role), role]);
                } else {
                  onChange(selected.filter((r) => r !== role));
                }
              }}
            />
            {checked ? (
              <CircleCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-signal" />
            ) : (
              <Circle aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />
            )}
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-micro font-medium">{meta.shortLabel}</span>
              <span className="truncate font-mono text-micro text-paper-low">{role}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function CreateUserSheet({ onClose }: { readonly onClose: () => void }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const create = useCreateOpsUser();
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(['ops-support']);
  const [feishuTenantKey, setFeishuTenantKey] = useState('');
  const [feishuOpenId, setFeishuOpenId] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        email: email.trim(),
        roles,
        feishu_tenant_key:
          feishuTenantKey.trim().length > 0 ? feishuTenantKey.trim() : undefined,
        feishu_open_id: feishuOpenId.trim().length > 0 ? feishuOpenId.trim() : undefined,
      });
      toast.success(t('ops_users.toast.created'));
      onClose();
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <OpsUserSheet
      title={t('ops_users.action.create')}
      titleId="ops-users-create-sheet-title"
      testId="ops-users-create-sheet"
      onClose={onClose}
    >
      <form
        className="flex min-h-0 flex-1 flex-col"
        data-testid="ops-users-create-form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          <FormField label={t('ops_users.form.email')} fieldId="ops-users-create-email">
            <Input
              id="ops-users-create-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              data-testid="ops-users-create-email"
              placeholder="name@nadoutong.com"
              required
            />
          </FormField>
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1.5 p-0 text-label text-paper-mid">
              {t('ops_users.form.roles')}
            </legend>
            <RolePicker selected={roles} onChange={setRoles} />
          </fieldset>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              label={t('ops_users.form.feishu_tenant')}
              fieldId="ops-users-create-feishu-tenant"
              className="min-w-0"
            >
              <Input
                id="ops-users-create-feishu-tenant"
                type="text"
                mono
                value={feishuTenantKey}
                onChange={(e) => setFeishuTenantKey(e.currentTarget.value)}
                data-testid="ops-users-create-feishu-tenant"
                placeholder="tenant_key"
              />
            </FormField>
            <FormField
              label={t('ops_users.form.feishu_open_id')}
              fieldId="ops-users-create-feishu-open-id"
              className="min-w-0"
            >
              <Input
                id="ops-users-create-feishu-open-id"
                type="text"
                mono
                value={feishuOpenId}
                onChange={(e) => setFeishuOpenId(e.currentTarget.value)}
                data-testid="ops-users-create-feishu-open-id"
                placeholder="open_id"
              />
            </FormField>
          </div>
          {error !== null ? (
            <p
              className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-line-soft px-6 py-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('ops_users.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={create.isPending || roles.length === 0}
            data-testid="ops-users-create-submit"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {create.isPending ? t('ops_users.action.creating') : t('ops_users.action.create')}
          </Button>
        </div>
      </form>
    </OpsUserSheet>
  );
}

/**
 * Sentinel for "no sales profile" in the brand picker. Distinct from
 * `null` (= operator has not touched the picker this session).
 */
const SALES_BRAND_NONE = '';

function EditUserSheet({
  user,
  onClose,
}: {
  readonly user: OpsUserRecord;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const update = useUpdateOpsUser();
  const salesProfile = useSalesProfile(user.id);
  const setSalesProfile = useSetSalesProfile();
  const deleteSalesProfile = useDeleteSalesProfile();
  const readOnlyRoles = user.roles.filter((role) => !ROLE_OPTIONS.includes(role));
  const [editEmail, setEditEmail] = useState(user.email);
  const [editRoles, setEditRoles] = useState<string[]>(
    user.roles.filter((role) => ROLE_OPTIONS.includes(role)),
  );
  const [editFeishuTenantKey, setEditFeishuTenantKey] = useState(user.feishu_tenant_key ?? '');
  const [editFeishuOpenId, setEditFeishuOpenId] = useState(user.feishu_open_id ?? '');
  const [editFeishuUnionId] = useState(user.feishu_union_id ?? '');
  const [editFeishuUserId] = useState(user.feishu_user_id ?? '');
  const [editFeishuName, setEditFeishuName] = useState(user.feishu_name ?? '');
  const [editFeishuAvatarUrl] = useState(user.feishu_avatar_url ?? '');
  const [editFeishuEnterpriseEmail] = useState(user.feishu_enterprise_email ?? '');
  /** `null` until the operator touches the brand picker this session. */
  const [brandSelection, setBrandSelection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadedBrand = salesProfile.data?.brand ?? SALES_BRAND_NONE;
  const effectiveBrand = brandSelection ?? loadedBrand;
  // A failed profile fetch is NOT "no profile" (#1098): the picker
  // stays non-interactive until a refetch succeeds, so an operator can
  // never save a brand change against unknown server state.
  const salesProfileUnavailable = salesProfile.isError;
  const salesProfileBusy =
    salesProfile.isLoading || setSalesProfile.isPending || deleteSalesProfile.isPending;

  async function handleSave(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const nextFeishuTenantKey = trimmedOrNull(editFeishuTenantKey);
    const nextFeishuOpenId = trimmedOrNull(editFeishuOpenId);
    const initialAssignableRoles = normalizeAssignableRoles(user.roles);
    const nextAssignableRoles = normalizeAssignableRoles(editRoles);
    const rolesChanged = !sameStringList(initialAssignableRoles, nextAssignableRoles);
    const feishuBindingUnchanged =
      nextFeishuTenantKey === user.feishu_tenant_key && nextFeishuOpenId === user.feishu_open_id;
    const feishuNameEdited = editFeishuName !== (user.feishu_name ?? '');
    try {
      await update.mutateAsync({
        id: user.id,
        email: editEmail.trim(),
        ...(rolesChanged ? { roles: nextAssignableRoles } : {}),
        feishu_tenant_key: nextFeishuTenantKey,
        feishu_open_id: nextFeishuOpenId,
        feishu_union_id: feishuBindingUnchanged ? trimmedOrNull(editFeishuUnionId) : null,
        feishu_user_id: feishuBindingUnchanged ? trimmedOrNull(editFeishuUserId) : null,
        feishu_name:
          feishuBindingUnchanged || feishuNameEdited ? trimmedOrNull(editFeishuName) : null,
        feishu_avatar_url: feishuBindingUnchanged ? trimmedOrNull(editFeishuAvatarUrl) : null,
        feishu_enterprise_email: feishuBindingUnchanged
          ? trimmedOrNull(editFeishuEnterpriseEmail)
          : null,
      });
      if (brandSelection !== null && brandSelection !== loadedBrand) {
        if (brandSelection === SALES_BRAND_NONE) {
          await deleteSalesProfile.mutateAsync(user.id);
        } else {
          await setSalesProfile.mutateAsync({ id: user.id, brand: brandSelection });
        }
      }
      toast.success(t('ops_users.toast.updated'));
      onClose();
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <OpsUserSheet
      title={user.display_name}
      titleId={`ops-users-edit-panel-${user.id}-title`}
      testId={`ops-users-edit-panel-${user.id}`}
      onClose={onClose}
    >
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(e) => void handleSave(e)}>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          <FormField label={t('ops_users.form.email')} fieldId={`ops-users-edit-email-${user.id}`}>
            <Input
              id={`ops-users-edit-email-${user.id}`}
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.currentTarget.value)}
              data-testid={`ops-users-edit-email-${user.id}`}
            />
          </FormField>
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1.5 p-0 text-label text-paper-mid">
              {t('ops_users.form.roles')}
            </legend>
            {readOnlyRoles.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {readOnlyRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center rounded-full border border-warn-line bg-warn-dim px-2 py-0.5 font-mono text-micro font-medium text-warn"
                  >
                    {role}
                  </span>
                ))}
              </div>
            ) : null}
            <RolePicker selected={editRoles} onChange={setEditRoles} disabled={update.isPending} />
          </fieldset>
          <fieldset
            className="m-0 flex flex-col gap-2 border-0 p-0"
            data-testid={`ops-users-edit-sales-profile-${user.id}`}
          >
            <legend className="mb-1.5 p-0 text-label text-paper-mid">
              {t('ops_users.form.sales_profile')}
            </legend>
            <p className="m-0 text-micro text-paper-low">
              {t('ops_users.form.sales_profile_hint')}
            </p>
            {salesProfileUnavailable ? (
              <div
                data-testid={`ops-users-edit-sales-profile-error-${user.id}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2"
              >
                <p className="m-0 text-micro text-bad-bright">
                  {t('ops_users.form.sales_profile_error')}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void salesProfile.refetch()}
                  data-testid={`ops-users-edit-sales-profile-retry-${user.id}`}
                >
                  {t('common.retry')}
                </Button>
              </div>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-3">
              {[SALES_BRAND_NONE, ...BRAND_IDS].map((brand) => {
                const checked = effectiveBrand === brand;
                const optionKey = brand === SALES_BRAND_NONE ? 'none' : brand;
                return (
                  <label
                    key={optionKey}
                    className={
                      checked
                        ? 'group relative flex min-w-0 cursor-pointer items-center gap-2 rounded-lg bg-signal-dim px-3 py-2 text-paper ring-1 ring-signal/60 transition-colors duration-120'
                        : 'group relative flex min-w-0 cursor-pointer items-center gap-2 rounded-lg bg-card px-3 py-2 text-paper-mid ring-1 ring-line transition-colors duration-120 hover:bg-active hover:text-paper'
                    }
                  >
                    <input
                      type="radio"
                      name={`ops-users-edit-sales-brand-${user.id}`}
                      className="peer sr-only"
                      checked={checked}
                      disabled={salesProfileBusy || salesProfileUnavailable || update.isPending}
                      onChange={() => setBrandSelection(brand)}
                      data-testid={`ops-users-edit-sales-brand-${optionKey}-${user.id}`}
                    />
                    {checked ? (
                      <CircleCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-signal" />
                    ) : (
                      <Circle aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />
                    )}
                    <span className="truncate font-mono text-micro font-medium">
                      {brand === SALES_BRAND_NONE
                        ? t('ops_users.form.sales_profile_none')
                        : brand}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              label={t('ops_users.form.feishu_tenant')}
              fieldId={`ops-users-edit-feishu-tenant-${user.id}`}
              className="min-w-0"
            >
              <Input
                id={`ops-users-edit-feishu-tenant-${user.id}`}
                type="text"
                mono
                value={editFeishuTenantKey}
                onChange={(e) => setEditFeishuTenantKey(e.currentTarget.value)}
                data-testid={`ops-users-edit-feishu-tenant-${user.id}`}
                placeholder="tenant_key"
              />
            </FormField>
            <FormField
              label={t('ops_users.form.feishu_open_id')}
              fieldId={`ops-users-edit-feishu-open-id-${user.id}`}
              className="min-w-0"
            >
              <Input
                id={`ops-users-edit-feishu-open-id-${user.id}`}
                type="text"
                mono
                value={editFeishuOpenId}
                onChange={(e) => setEditFeishuOpenId(e.currentTarget.value)}
                data-testid={`ops-users-edit-feishu-open-id-${user.id}`}
                placeholder="open_id"
              />
            </FormField>
          </div>
          <FormField
            label={t('ops_users.form.feishu_name')}
            fieldId={`ops-users-edit-feishu-name-${user.id}`}
          >
            <Input
              id={`ops-users-edit-feishu-name-${user.id}`}
              type="text"
              value={editFeishuName}
              onChange={(e) => setEditFeishuName(e.currentTarget.value)}
              data-testid={`ops-users-edit-feishu-name-${user.id}`}
            />
          </FormField>
          {error !== null ? (
            <p
              className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-line-soft px-6 py-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('ops_users.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              update.isPending ||
              setSalesProfile.isPending ||
              deleteSalesProfile.isPending ||
              (editRoles.length === 0 && readOnlyRoles.length === 0)
            }
            data-testid={`ops-users-save-${user.id}`}
          >
            {update.isPending || setSalesProfile.isPending || deleteSalesProfile.isPending
              ? t('ops_users.action.saving')
              : t('common.save')}
          </Button>
        </div>
      </form>
    </OpsUserSheet>
  );
}

function ResetPasswordModal({
  user,
  onClose,
}: {
  readonly user: OpsUserRecord;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const reset = useResetOpsUserPassword();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleReset(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await reset.mutateAsync(user.id);
      toast.success(t('ops_users.toast.reset'));
      onClose();
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={`ops-users-reset-title-${user.id}`}
      testId={`ops-users-reset-confirm-${user.id}`}
      closeOnBackdrop={!reset.isPending}
      panelClassName="max-w-xl rounded-xl"
    >
      <form className="p-6" onSubmit={(e) => void handleReset(e)}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="m-0 text-label font-medium text-bad">{t('ops_users.reset.eyebrow')}</p>
            <h3
              id={`ops-users-reset-title-${user.id}`}
              className="m-0 mt-2 text-title text-paper"
            >
              {t('ops_users.action.reset_password')}
            </h3>
          </div>
          <IconButton size="sm" onClick={onClose} disabled={reset.isPending} label={t('common.close')}>
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </div>
        <p className="m-0 mt-5 text-body text-paper-mid">
          {t('ops_users.reset.confirm_prompt', { email: user.email })}
        </p>
        <Input
          type="text"
          mono
          value={typed}
          onChange={(e) => setTyped(e.currentTarget.value)}
          data-testid={`ops-users-reset-typed-${user.id}`}
          placeholder={user.email}
          className="mt-4"
        />
        {error !== null ? (
          <p
            className="m-0 mt-4 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={reset.isPending}
            data-testid={`ops-users-reset-cancel-${user.id}`}
          >
            {t('ops_users.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={reset.isPending || typed !== user.email}
            data-testid={`ops-users-reset-submit-${user.id}`}
          >
            {reset.isPending ? t('ops_users.action.resetting') : t('ops_users.action.reset_confirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SetPasswordModal({
  user,
  onClose,
}: {
  readonly user: OpsUserRecord;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const setPasswordMut = useSetOpsUserPassword();
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (
      password.length < OPS_PASSWORD_MIN_LENGTH ||
      password.length > OPS_PASSWORD_MAX_LENGTH
    ) {
      setError(
        t('ops_users.set_password.error.length', {
          min: String(OPS_PASSWORD_MIN_LENGTH),
          max: String(OPS_PASSWORD_MAX_LENGTH),
        }),
      );
      return;
    }
    if (password !== confirm) {
      setError(t('ops_users.set_password.error.mismatch'));
      return;
    }
    try {
      await setPasswordMut.mutateAsync({ id: user.id, password });
      toast.success(t('ops_users.toast.set_password'));
      onClose();
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={`ops-users-set-password-title-${user.id}`}
      testId={`ops-users-set-password-confirm-${user.id}`}
      closeOnBackdrop={!setPasswordMut.isPending}
      panelClassName="max-w-xl rounded-xl"
    >
      <form className="p-6" onSubmit={(e) => void handleSubmit(e)}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="m-0 text-label font-medium text-paper-low">
              {t('ops_users.set_password.eyebrow')}
            </p>
            <h3
              id={`ops-users-set-password-title-${user.id}`}
              className="m-0 mt-2 text-title text-paper"
            >
              {t('ops_users.action.set_password')}
            </h3>
          </div>
          <IconButton
            size="sm"
            onClick={onClose}
            disabled={setPasswordMut.isPending}
            label={t('common.close')}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </div>
        <p className="m-0 mt-5 text-body text-paper-mid">
          {t('ops_users.set_password.prompt', { email: user.email })}
        </p>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPasswordValue(e.currentTarget.value)}
          data-testid={`ops-users-set-password-value-${user.id}`}
          placeholder={t('ops_users.set_password.password')}
          className="mt-4"
        />
        <Input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          data-testid={`ops-users-set-password-confirm-value-${user.id}`}
          placeholder={t('ops_users.set_password.confirm')}
          className="mt-3"
        />
        {error !== null ? (
          <p
            className="m-0 mt-4 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
            role="alert"
            data-testid={`ops-users-set-password-error-${user.id}`}
          >
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={setPasswordMut.isPending}
            data-testid={`ops-users-set-password-cancel-${user.id}`}
          >
            {t('ops_users.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={setPasswordMut.isPending}
            data-testid={`ops-users-set-password-submit-${user.id}`}
          >
            {setPasswordMut.isPending
              ? t('ops_users.action.setting_password')
              : t('ops_users.action.set_password')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function InvitePasswordModal({
  user,
  onClose,
}: {
  readonly user: OpsUserRecord;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const inviteMut = useInviteOpsUserPassword();
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await inviteMut.mutateAsync(user.id);
      toast.success(t('ops_users.toast.invite_password'));
      onClose();
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={`ops-users-invite-password-title-${user.id}`}
      testId={`ops-users-invite-password-confirm-${user.id}`}
      closeOnBackdrop={!inviteMut.isPending}
      panelClassName="max-w-xl rounded-xl"
    >
      <form className="p-6" onSubmit={(e) => void handleSubmit(e)}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="m-0 text-label font-medium text-paper-low">
              {t('ops_users.invite_password.eyebrow')}
            </p>
            <h3
              id={`ops-users-invite-password-title-${user.id}`}
              className="m-0 mt-2 text-title text-paper"
            >
              {t('ops_users.action.invite_password')}
            </h3>
          </div>
          <IconButton
            size="sm"
            onClick={onClose}
            disabled={inviteMut.isPending}
            label={t('common.close')}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </div>
        <p className="m-0 mt-5 text-body text-paper-mid">
          {t('ops_users.invite_password.prompt', { email: user.email })}
        </p>
        {error !== null ? (
          <p
            className="m-0 mt-4 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
            role="alert"
            data-testid={`ops-users-invite-password-error-${user.id}`}
          >
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={inviteMut.isPending}
            data-testid={`ops-users-invite-password-cancel-${user.id}`}
          >
            {t('ops_users.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={inviteMut.isPending}
            data-testid={`ops-users-invite-password-submit-${user.id}`}
          >
            {inviteMut.isPending
              ? t('ops_users.action.inviting_password')
              : t('ops_users.action.invite_password')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface UsersTableProps {
  readonly users: readonly OpsUserRecord[];
  readonly selectedUserId: string | null;
  readonly directPasswordSetAllowed: boolean;
  readonly onSelect: (id: string) => void;
  readonly onEdit: (user: OpsUserRecord) => void;
  readonly onReset: (user: OpsUserRecord) => void;
  readonly onSetPassword: (user: OpsUserRecord) => void;
  readonly onInvite: (user: OpsUserRecord) => void;
}

function UsersTable({
  users,
  selectedUserId,
  directPasswordSetAllowed,
  onSelect,
  onEdit,
  onReset,
  onSetPassword,
  onInvite,
}: UsersTableProps): JSX.Element {
  const t = useT();
  return (
    <Table testId="ops-users-table" className="[&>table]:table-fixed [&>table]:min-w-[1240px]">
      <THead>
        <Tr interactive={false}>
          <Th className="w-[204px]">{t('ops_users.column.user')}</Th>
          {ROLE_OPTIONS.map((role) => (
            <Th key={role} className="w-[54px] px-0 text-center" title={role}>
              {roleMeta(role).matrixLabel}
            </Th>
          ))}
          <Th className="w-[178px]">{t('ops_users.column.feishu')}</Th>
          <Th className="w-[132px]">{t('ops_users.column.status')}</Th>
          <Th className="w-[114px]">{t('ops_users.column.created_at')}</Th>
          <Th className="w-[144px] px-2 text-right">{t('ops_users.column.actions')}</Th>
        </Tr>
      </THead>
      <TBody>
        {users.map((u) => (
          <UserRow
            key={u.id}
            user={u}
            selected={selectedUserId === u.id}
            onSelect={() => onSelect(u.id)}
            onEdit={() => onEdit(u)}
            onReset={() => onReset(u)}
            onSetPassword={() => onSetPassword(u)}
            onInvite={() => onInvite(u)}
            directPasswordSetAllowed={directPasswordSetAllowed}
          />
        ))}
      </TBody>
    </Table>
  );
}

interface UserRowProps {
  readonly user: OpsUserRecord;
  readonly selected: boolean;
  readonly directPasswordSetAllowed: boolean;
  readonly onSelect: () => void;
  readonly onEdit: () => void;
  readonly onReset: () => void;
  readonly onSetPassword: () => void;
  readonly onInvite: () => void;
}

function UserRow({
  user,
  selected,
  directPasswordSetAllowed,
  onSelect,
  onEdit,
  onReset,
  onSetPassword,
  onInvite,
}: UserRowProps): JSX.Element {
  const t = useT();
  const disable = useDisableOpsUser();
  const enable = useEnableOpsUser();
  const revoke = useRevokeOpsUserSessions();
  const toast = useToast();
  const isDisabled = user.disabled_at !== null;
  const effectiveRoleSet = new Set(expandOpsRoles(user.roles));
  const busy = disable.isPending || enable.isPending || revoke.isPending;
  const [error, setError] = useState<string | null>(null);

  async function handleDisable(): Promise<void> {
    setError(null);
    try {
      await disable.mutateAsync(user.id);
      toast.success(t('ops_users.toast.disabled'));
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  async function handleEnable(): Promise<void> {
    setError(null);
    try {
      await enable.mutateAsync(user.id);
      toast.success(t('ops_users.toast.enabled'));
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  async function handleRevoke(): Promise<void> {
    setError(null);
    try {
      await revoke.mutateAsync(user.id);
      toast.success(t('ops_users.toast.revoked'));
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <>
      <Tr
        className={selected ? 'cursor-pointer bg-active' : 'cursor-pointer'}
        data-testid={`ops-users-row-${user.id}`}
        data-disabled={isDisabled ? 'true' : 'false'}
        onClick={onSelect}
      >
        <Td>
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className={selected ? 'h-8 w-1 rounded-full bg-signal' : 'h-8 w-1 rounded-full bg-transparent'}
            />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span
                className="truncate text-dense font-medium text-paper"
                data-testid={`ops-users-display-name-${user.id}`}
                title={user.display_name}
              >
                {user.display_name}
              </span>
              <span className="truncate font-mono text-micro text-paper-low" title={user.email}>
                {user.email}
              </span>
            </div>
          </div>
        </Td>
        {ROLE_OPTIONS.map((role) => {
          const enabled = effectiveRoleSet.has(role);
          return (
            <Td key={role} className="px-0 text-center">
              <span
                className={
                  enabled
                    ? 'inline-flex h-7 w-7 items-center justify-center rounded-full bg-signal-dim text-signal'
                    : 'inline-flex h-7 w-7 items-center justify-center rounded-full bg-raised text-paper-low'
                }
                title={role}
              >
                <span className="sr-only">{role}</span>
                {enabled ? (
                  <Check aria-hidden="true" className="h-3.5 w-3.5" />
                ) : (
                  <Minus aria-hidden="true" className="h-3.5 w-3.5" />
                )}
              </span>
            </Td>
          );
        })}
        <Td>
          {user.feishu_open_id === null ? (
            <span className="text-micro text-paper-low">{t('ops_users.feishu.unbound')}</span>
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              {user.feishu_avatar_url !== null ? (
                <img
                  src={user.feishu_avatar_url}
                  alt=""
                  className="h-7 w-7 rounded-full"
                  loading="lazy"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-raised text-micro font-semibold text-signal">
                  {feishuDisplayName(user).slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="flex min-w-0 flex-col gap-0.5">
                <span
                  className="truncate text-dense font-medium text-paper"
                  title={feishuDisplayName(user)}
                >
                  {feishuDisplayName(user)}
                </span>
                <span className="truncate font-mono text-micro text-paper-low" title={user.feishu_open_id}>
                  {user.feishu_open_id}
                </span>
              </div>
            </div>
          )}
        </Td>
        <Td>
          <StatusChip variant={statusVariant(user)} label={t(statusLabelKey(user))} />
        </Td>
        <Td mono>{formatLocalTimestamp(user.created_at, { seconds: true })}</Td>
        <Td className="px-2 text-right">
          <div className="flex items-center justify-end gap-1">
            <IconButton
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              disabled={busy}
              data-testid={`ops-users-edit-${user.id}`}
              label={t('ops_users.action.edit')}
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
            <IconButton
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                void handleRevoke();
              }}
              disabled={busy}
              data-testid={`ops-users-revoke-${user.id}`}
              label={revoke.isPending ? t('ops_users.action.revoking') : t('ops_users.action.revoke')}
            >
              <LogOut aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
            {directPasswordSetAllowed ? (
              <IconButton
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetPassword();
                }}
                disabled={busy}
                data-testid={`ops-users-set-password-${user.id}`}
                label={t('ops_users.action.set_password')}
              >
                <Lock aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            ) : null}
            <IconButton
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onInvite();
              }}
              disabled={busy}
              data-testid={`ops-users-invite-password-${user.id}`}
              label={t('ops_users.action.invite_password')}
            >
              <Mail aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
            <IconButton
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
              disabled={busy}
              data-testid={`ops-users-reset-${user.id}`}
              label={t('ops_users.action.reset_password')}
            >
              <KeyRound aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
            {isDisabled ? (
              <IconButton
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleEnable();
                }}
                disabled={busy}
                data-testid={`ops-users-enable-${user.id}`}
                label={enable.isPending ? t('ops_users.action.enabling') : t('ops_users.action.enable')}
              >
                <Play aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            ) : (
              <IconButton
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDisable();
                }}
                disabled={busy}
                data-testid={`ops-users-disable-${user.id}`}
                label={disable.isPending ? t('ops_users.action.disabling') : t('ops_users.action.disable')}
                className="text-warn hover:bg-warn-dim"
              >
                <Ban aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            )}
          </div>
        </Td>
      </Tr>
      {error !== null ? (
        <Tr interactive={false}>
          <Td colSpan={ROLE_OPTIONS.length + 5} className="pb-2 text-right text-micro text-bad">
            {error}
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

function SelectedUserSheet({
  user,
  onClose,
  onEdit,
  onReset,
  onSetPassword,
  onInvite,
  directPasswordSetAllowed,
}: {
  readonly user: OpsUserRecord;
  readonly onClose: () => void;
  readonly onEdit: (user: OpsUserRecord) => void;
  readonly onReset: (user: OpsUserRecord) => void;
  readonly onSetPassword: (user: OpsUserRecord) => void;
  readonly onInvite: (user: OpsUserRecord) => void;
  readonly directPasswordSetAllowed: boolean;
}): JSX.Element {
  const t = useT();
  const effectiveRoles = expandOpsRoles(user.roles);
  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={`ops-users-detail-sheet-${user.id}-title`}
      containerClassName="items-stretch justify-end px-0"
      panelClassName="h-full max-w-2xl rounded-none"
      testId={`ops-users-detail-sheet-${user.id}`}
    >
      <aside className="flex h-full flex-col">
        <div className="border-b border-line-soft px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              {user.feishu_avatar_url !== null ? (
                <img src={user.feishu_avatar_url} alt="" className="h-12 w-12 rounded-full" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-active text-body font-semibold text-signal">
                  {user.display_name.slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="m-0 text-label text-paper-low">{t('ops_users.sheet.eyebrow')}</p>
                <h3
                  id={`ops-users-detail-sheet-${user.id}-title`}
                  className="m-0 mt-1 truncate text-title text-paper"
                >
                  {user.display_name}
                </h3>
                <p className="m-0 truncate font-mono text-micro text-paper-low">{user.email}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-start gap-2">
              <StatusChip variant={statusVariant(user)} label={t(statusLabelKey(user))} />
              <IconButton onClick={onClose} label={t('common.close')}>
                <X aria-hidden="true" className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          <section className="flex flex-col gap-3 rounded-lg bg-card p-4">
            <h4 className="m-0 text-label text-paper-low">{t('ops_users.inspector.roles')}</h4>
            <div className="flex flex-wrap gap-2">
              {effectiveRoles.map((role) => {
                const RoleIcon = roleMeta(role).icon;
                return (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-3 py-1 font-mono text-micro font-medium text-paper"
                  >
                    <RoleIcon aria-hidden="true" className="h-3.5 w-3.5 text-paper-low" />
                    {role}
                  </span>
                );
              })}
            </div>
          </section>

          <section className="flex flex-col gap-3 rounded-lg bg-card p-4">
            <h4 className="m-0 text-label text-paper-low">{t('ops_users.inspector.feishu')}</h4>
            <dl className="m-0 grid gap-3 text-dense">
              <InspectorRow label={t('ops_users.form.feishu_tenant')} value={user.feishu_tenant_key ?? t('ops_users.feishu.unbound')} mono />
              <InspectorRow label={t('ops_users.form.feishu_open_id')} value={user.feishu_open_id ?? t('ops_users.feishu.unbound')} mono />
              <InspectorRow label={t('ops_users.form.feishu_name')} value={feishuDisplayName(user) || t('ops_users.feishu.unbound')} />
            </dl>
          </section>

          <section className="flex flex-col gap-3 rounded-lg bg-card p-4">
            <h4 className="m-0 text-label text-paper-low">{t('ops_users.inspector.security')}</h4>
            <dl className="m-0 grid gap-3 text-dense">
              <InspectorRow
                label={t('ops_users.inspector.password')}
                value={
                  user.has_password
                    ? t('ops_users.inspector.password_set')
                    : t('ops_users.inspector.password_unset')
                }
              />
              <InspectorRow
                label={t('ops_users.inspector.revoked_before')}
                value={
                  user.session_revoked_before === null
                    ? '-'
                    : formatLocalTimestamp(user.session_revoked_before, { seconds: true })
                }
                mono
              />
              <InspectorRow
                label={t('ops_users.column.created_at')}
                value={formatLocalTimestamp(user.created_at, { seconds: true })}
                mono
              />
            </dl>
          </section>
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-line-soft px-6 py-4">
          <Button type="button" onClick={() => onEdit(user)}>
            <Pencil aria-hidden="true" className="h-4 w-4" />
            {t('ops_users.action.edit')}
          </Button>
          {directPasswordSetAllowed ? (
            <Button type="button" onClick={() => onSetPassword(user)}>
              <Lock aria-hidden="true" className="h-4 w-4" />
              {t('ops_users.action.set_password')}
            </Button>
          ) : null}
          <Button type="button" onClick={() => onInvite(user)}>
            <Mail aria-hidden="true" className="h-4 w-4" />
            {t('ops_users.action.invite_password')}
          </Button>
          <Button type="button" variant="danger" onClick={() => onReset(user)}>
            <KeyRound aria-hidden="true" className="h-4 w-4" />
            {t('ops_users.action.reset_password')}
          </Button>
        </div>
      </aside>
    </Modal>
  );
}

function InspectorRow({
  label,
  value,
  mono = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-paper-low">{label}</dt>
      <dd className={`m-0 max-w-[220px] truncate text-paper-mid ${mono ? 'font-mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
